<template>
  <div>
    <div class="library-modal-content" :class="{ 'library-compact': compact }">
      <div class="video-library-header">
        <div class="video-library-title-wrapper">
          <h1 class="video-library-title">
            {{ getVideoLibrary.length }}
            {{ $t("create.step2.videoLibraryModal.title") }}
          </h1>
          <span v-if="selectedVideos.length" class="selected-videos">
            -
            <span class="purple-selected">
              {{ selectedVideos.length }}
              {{ $t("create.step2.videoLibraryModal.selected") }}
            </span>
            <SquaredCrossIcon
              class="square-cross-icon"
              @click="deselectAllVideos"
            />
          </span>
        </div>
        <div class="search-filter-sort-wrapper">
          <SearchBar
            class="library-search-bar"
            :preselected-query="preselectedQuery"
            @query="getQuery"
          />
          <div
            class="filter-sort-button"
            :class="{ 'selected-filter-sort-button': filtersActive }"
            @click="openFilterModal"
          >
            <div class="filter-icon">
              <FilterIcon class="filter-svg" />
              <span v-if="numberOfFilters > 0" class="number-filters">{{
                numberOfFilters
              }}</span>
            </div>
            <p class="filter-sort-by">
              {{ $t("campaignManager.filter.filters") }}
            </p>
          </div>
          <div
            class="filter-sort-button sort-button"
            :class="{ 'selected-filter-sort-button': sortingActive }"
            @click="openSortingModal"
          >
            <div class="sort-icon">
              <div class="sort-arrows">
                <SortArrow />
                <SortArrow class="reverse-arrow" />
              </div>
              <span v-if="numberOfSorts" class="number-sorting">1</span>
            </div>
            <p class="filter-sort-by">{{ $t("campaignManager.sort") }}</p>
          </div>
        </div>
      </div>
      <div v-if="showNoVideosSnippet" class="no-videos-snippet-wrapper">
        <div class="no-videos-snippet">
          <div class="no-videos-wrapper">
            <div class="no-videos-text">
              <h2>{{ $t("onboarding.addVideoSuggestions.noVideos") }}</h2>
              <p>{{ $t("onboarding.addVideoSuggestions.suggestion") }}</p>
              <p>
                <span>{{ $t("onboarding.addVideoSuggestions.title1") }}</span>
                {{ $t("onboarding.addVideoSuggestions.text1") }}
              </p>
              <p>
                <span>{{ $t("onboarding.addVideoSuggestions.title2") }}</span>
                {{ $t("onboarding.addVideoSuggestions.text2") }}
              </p>
            </div>
            <img
              class="no-video-img"
              src="@/assets/proposedVideos.png"
              alt="proposed-videos"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="video-library-cards"
        :class="{ 'few-video-cards': filteredVideoLibrary.length <= 3 }"
      >
        <div
          v-for="video in filteredVideoLibrary"
          :key="video._id"
          class="video-card"
          :class="{ selected: selectedVideos.includes(video._id) }"
          @click="toggleSelectedVideo(video._id)"
        >
          <div class="video-image">
            <div
              v-if="selectedVideos.includes(video._id)"
              class="selection-overlay"
            ></div>
            <div
              v-if="selectedVideos.includes(video._id)"
              class="purple-circle"
            ></div>
            <img
              v-if="video.thumbnail"
              :src="thumbnailSrc(video)"
              alt="thumbnail"
              loading="lazy"
              @error="handleThumbError(video)"
              @load="handleThumbLoad(video)"
            />
            <div v-else class="no-thumbnail">
              <p>Thumbnail not available 😢</p>
            </div>
            <PlayIcon
              v-if="!isForReplaceVideo"
              id="play-icon"
              v-tippy="{ placement: 'top', arrow: true }"
              class="play-icon"
              :class="{
                'play-icon-selected-video': selectedVideos.includes(video._id),
              }"
              content="Preview video"
              @click.stop="$emit('play-video-in-preview', video)"
            />
            <div
              v-if="Array.isArray(video.chapters) && video.chapters.length > 0"
              v-tippy="{
                arrow: true,
                placement: 'top',
                a11y: false,
              }"
              class="video-details chapters"
              :class="{ 'moved-chapters': hasLanguageSettings }"
              content="Edit chapters"
              @click="openEditChaptersModal(video)"
            >
              <AddChaptersIcon />
              <span>{{ video?.chapters.length }}</span>
            </div>
            <div
              v-if="hasLanguageSettings"
              class="video-details globe-details"
              @click="openLocalesModal(video)"
            >
              <img
                v-if="!video.language"
                src="@/assets/globe-icon.png"
                alt="globe"
                class="globe-icon"
              />
              <img
                v-else
                :src="getLocaleFlag(video.language)"
                alt="flag"
                class="globe-icon"
              />
            </div>
            <div
              v-if="!isForReplaceVideo"
              v-tippy="{
                arrow: true,
                placement: 'top',
                a11y: false,
              }"
              class="video-details products-tagged"
              :content="$t('create.step2.videoLibraryModal.seeProductsTagged')"
              @click="openProductListModal(video)"
            >
              <TagProductIcon />
              <span>{{ productIdsLength(video._id) }}</span>
            </div>
            <div
              v-tippy="{
                arrow: true,
                placement: 'top',
                a11y: false,
              }"
              class="video-details video-views"
              :content="$t('campaignManager.sortOptions.last7DaysViews')"
            >
              <Visibility />
              <span>{{ video.views }}</span>
            </div>
            <div class="video-details video-duration">
              <span>{{ formatDuration(video.videoDuration) }}</span>
            </div>

            <div
              v-if="Array.isArray(video.tags) && video.tags.length > 0"
              v-tippy="{
                arrow: true,
                placement: 'top',
                a11y: false,
              }"
              class="video-details video-tags"
              :content="video.tags.length + ' tags'"
              @click="openAddTagsModal(video)"
            >
              <AddTag />
              <span>{{ video.tags.length }}</span>
            </div>
          </div>
          <div class="video-card-text">
            <div
              v-if="editingVideoId === video._id"
              class="file-name-container"
            >
              <textarea
                ref="videoFilenameTextarea"
                :key="video._id"
                v-model="editedFileName"
                class="video-filename-textarea"
                @mousedown.stop
                @blur="handleFileNameUpdate(video._id)"
                @keydown.enter.prevent="handleEnterKey($event, video._id)"
              ></textarea>
            </div>
            <div v-else class="file-name-container">
              <h2
                class="video-card-title"
                @click.stop="startEditing(video._id)"
              >
                {{ displayedFileName(video) }}
              </h2>
            </div>
            <p class="video-card-indication">
              <span>{{ $t("create.step2.videoLibraryModal.imported") }}</span>
              {{ videoLibraryDate(video.creationDate) }}
            </p>
          </div>
        </div>
      </div>
      <div v-if="showButtons" class="buttons">
        <button class="button-primary --grey" @click="$emit('close-library')">
          {{ $t("shared.buttons.back") }}
        </button>
        <button class="button-primary" @click="handleImport">
          {{ $t("create.step2.videoLibraryModal.import") }}
        </button>
      </div>
    </div>
    <SpinnerLoader v-if="isLoading" />
    <VideoFilter
      v-if="showVideoFilter"
      :initial-tags="activeFilters.tags"
      :initial-published="activeFilters.published"
      :initial-products-tagged="activeFilters.productsTagged"
      @filter="handleFilter"
      @reset="handleFilterReset"
      @close-filter-modal="closeFilterModal"
    />
    <VideoSort
      v-if="showVideoSort"
      :initial-sorting="activeSorting"
      :initial-order="activeOrder"
      @sort="handleSort"
      @close-sorting-modal="closeSortingModal"
    />
  </div>
</template>

<script>
import PlayIcon from "@/assets/svg/play-icon.svg?inline";
import SortArrow from "@/assets/svg/sort-arrow.svg?inline";
import { mapGetters, mapState } from "vuex";
import SearchBar from "@/components/shared/SearchBar.vue";
import SpinnerLoader from "@/components/shared/SpinnerLoader.vue";
import Visibility from "@/assets/svg/visibility-reverse.svg?inline";
import TagProductIcon from "@/assets/svg/tag-product-icon-small.svg?inline";
import enums from "../../enums";
import { normalizeString } from "@/utils/stringNormalize";
import SquaredCrossIcon from "@/assets/svg/squared-cross-icon.svg?inline";
import AddChaptersIcon from "@/assets/svg/split-small.svg?inline";
import FilterIcon from "@/assets/svg/filter-icon.svg?inline";
import AddTag from "@/assets/svg/add-tag-small.svg?inline";
import VideoFilter from "@/components/shared/VideoFilter.vue";
import VideoSort from "@/components/shared/VideoSort.vue";
const { FormatType } = enums;
import { locales } from "@/utils/locales";

export default {
  components: {
    PlayIcon,
    SearchBar,
    SpinnerLoader,
    SortArrow,
    Visibility,
    TagProductIcon,
    SquaredCrossIcon,
    AddChaptersIcon,
    FilterIcon,
    AddTag,
    VideoFilter,
    VideoSort,
  },
  props: {
    showButtons: {
      type: Boolean,
      default: true,
    },
    preselectedQuery: {
      type: String,
      default: "",
    },
    selectedVideosProp: {
      type: Array,
      default: () => [],
    },
    isForReplaceVideo: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    retryOnlyIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      videoList: [],
      query: "",
      selectedVideos: [],
      FormatType,
      isLoading: false,
      slideDirection: "right",
      sortingField: "creationDate",
      sortingOrder: "desc",
      editedFileName: "",
      editingVideoId: null,
      originalFileNameExtension: "",
      showAddTagsToVideosModal: false,
      filtersActive: false,
      sortingActive: false,
      showVideoFilter: false,
      activeFilters: {
        tags: [],
        published: null,
        productsTagged: null,
      },
      showVideoSort: false,
      activeSorting: null,
      activeOrder: null,
      locales,
      tempThumbsById: {},
      thumbRetryCount: {},
    };
  },
  computed: {
    ...mapState(["site"]),
    ...mapGetters({
      getSiteId: "account/getSiteId",
      getVideoLibrary: "video/getVideoLibrary",
      videoObject: "campaign/videoObject",
      tagsArray: "tag/getTags",
    }),

    productIdsLength() {
      return (videoId) => {
        const video = this.getVideoLibrary.find((v) => v._id === videoId);
        return video?.productIds?.length || 0;
      };
    },
    showNoVideosSnippet() {
      return this.getVideoLibrary.length === 0;
    },
    filteredVideoLibrary() {
      let list = this.getVideoLibrary;

      if (this.query) {
        const q = normalizeString(this.query.toLowerCase());
        list = list.filter((video) =>
          normalizeString(video.fileName.toLowerCase()).includes(q)
        );
      }

      if (this.activeFilters.tags.length) {
        const selectedIds = this.activeFilters.tags.map((tag) => tag._id);
        list = list.filter(
          (video) =>
            Array.isArray(video.tags) &&
            video.tags.some((tagId) => selectedIds.includes(tagId))
        );
      }

      if (this.activeFilters.published === "live") {
        list = list.filter((video) => video.views > 0);
      } else if (this.activeFilters.published === "not_live") {
        list = list.filter((video) => video.views === 0);
      }

      if (this.activeFilters.productsTagged === "have_products") {
        list = list.filter(
          (video) =>
            Array.isArray(video.productIds) && video.productIds.length > 0
        );
      } else if (this.activeFilters.productsTagged === "have_not") {
        list = list.filter(
          (video) =>
            !Array.isArray(video.productIds) || video.productIds.length === 0
        );
      }

      if (this.activeSorting) {
        list = [...list].sort((a, b) => {
          let aVal, bVal;
          switch (this.activeSorting) {
            case "creationDate":
              aVal = new Date(a.creationDate).getTime();
              bVal = new Date(b.creationDate).getTime();
              break;
            case "duration":
              aVal = a.videoDuration;
              bVal = b.videoDuration;
              break;
            case "views":
              aVal = a.views;
              bVal = b.views;
              break;
            case "productsCount":
              aVal = (a.productIds || []).length;
              bVal = (b.productIds || []).length;
              break;
            default:
              return 0;
          }
          return this.activeOrder === "asc" ? aVal - bVal : bVal - aVal;
        });
      }

      return list;
    },
    numberOfFilters() {
      let count = 0;
      count += this.activeFilters.tags.length;
      if (this.activeFilters.published) count += 1;
      if (this.activeFilters.productsTagged) count += 1;
      return count;
    },
    numberOfSorts() {
      return this.activeSorting ? 1 : 0;
    },
    hasLanguageSettings() {
      const ls = this.site.site.languageSettings;
      return ls != null && Object.keys(ls).length > 0;
    },
    thumbnailSrc() {
      return (video) => this.tempThumbsById[video._id] || video.thumbnail;
    },
    retryOnlyIdsSet() {
      return new Set(this.retryOnlyIds);
    },
  },
  watch: {
    getVideoLibrary: {
      handler() {
        this.videoList = this.getVideoLibrary;
      },
      immediate: true,
    },
    selectedVideos: {
      handler(newVal) {
        this.$emit("are-videos-selected", newVal.length > 0, newVal.length);
      },
      deep: true,
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocMouseDown, true);
  },
  async created() {
    this.isLoading = true;
    await this.$store.dispatch("video/getVideoLibrary", {
      siteId: this.getSiteId,
      query: {
        $or: [{ isPremade: { $exists: false } }, { isPremade: false }],
        $and: [
          {
            fileName: { $exists: true },
            height: { $exists: true },
            width: { $exists: true },
            videoDuration: { $exists: true },
            url: { $not: { $regex: "blob:" } },
          },
        ],
        getAnalytics: true,
      },
    });
    this.query = this.preselectedQuery;
    this.filterListOfVideos();
    this.isLoading = false;
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocMouseDown, true);
  },
  methods: {
    videoLibraryDate(date) {
      return (
        date.slice(8, 10) + "/" + date.slice(5, 7) + "/" + date.slice(2, 4)
      );
    },

    formatDuration(duration) {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.trunc(duration - minutes * 60);
      return `${minutes}:${seconds < 10 ? "0" + seconds : seconds}`;
    },
    getQuery(payload) {
      this.query = payload;
      this.filterListOfVideos();
    },
    filterListOfVideos() {
      const normalizedQuery = normalizeString(this.query.toLowerCase());
      this.videoList = this.getVideoLibrary.filter((video) => {
        const normalizedFileName = normalizeString(
          video.fileName.toLowerCase()
        );
        return normalizedFileName.includes(normalizedQuery);
      });
    },
    toggleSelectedVideo(videoId) {
      if (this.isForReplaceVideo) {
        if (this.selectedVideos.includes(videoId)) {
          this.selectedVideos = [];
        } else {
          this.selectedVideos = [videoId];
        }
      } else {
        if (!this.selectedVideos.includes(videoId)) {
          const selectedVideo = this.getVideoLibrary.find(
            (video) => video._id === videoId
          );
          this.selectedVideos.push(videoId);
          this.$emit("play-video-in-preview", selectedVideo);
        } else {
          const index = this.selectedVideos.indexOf(videoId);
          this.selectedVideos.splice(index, 1);
        }
      }
      this.$emit("update:selectedVideos", this.selectedVideos);
    },

    handleImport() {
      const videosToImport = [];
      this.selectedVideos.forEach((videoId) => {
        const foundVideo = this.getVideoLibrary.find(
          (video) => video._id === videoId
        );
        videosToImport.push(foundVideo);
      });
      this.$emit("import-videos", structuredClone(videosToImport));
    },
    closeLibrary() {
      this.$emit("close-library");
    },
    sortByField(field) {
      if (field === this.sortingField) {
        this.sortingOrder = this.sortingOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortingField = field;
        this.sortingOrder = "asc";
      }
    },
    startEditing(videoId) {
      const video = this.getVideoLibrary.find((video) => video._id === videoId);
      const fileName = video.fileName;
      const extensionIndex = fileName.lastIndexOf(".");
      const extension =
        extensionIndex !== -1 ? fileName.substring(extensionIndex) : "";
      this.editedFileName =
        extensionIndex !== -1
          ? fileName.substring(0, extensionIndex)
          : fileName;
      this.editingVideoId = videoId;
      this.originalFileNameExtension = extension;
      this.$nextTick(() => {
        const textarea = document.querySelector(".video-filename-textarea");
        if (textarea) {
          textarea.focus();
        }
      });
    },
    async handleFileNameUpdate(videoId) {
      const video = this.getVideoLibrary.find((v) => v._id === videoId);
      if (!video) {
        this.editingVideoId = null;
        return;
      }

      const current = (video.fileName || "").replace(/\.[^/.]+$/, "");
      const newName = (this.editedFileName || "").trim();

      if (!newName || newName === current) {
        this.editingVideoId = null;
        return;
      }

      const newFileName = newName + this.originalFileNameExtension;

      try {
        await this.$store.dispatch("video/updateVideoLibraryFileName", {
          videoId,
          fileName: newFileName,
        });
        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("create.step2.fileNameUpdated"),
          type: "success",
        });
      } catch (e) {
        console.error("Error updating file name:", e);
      } finally {
        this.editingVideoId = null;
      }
    },
    displayedFileName(video) {
      if (video && video.fileName) {
        if (this.editingVideoId === video._id) {
          return this.editedFileName;
        } else {
          return video.fileName.replace(/\.\w+$/, "");
        }
      }
      return "";
    },
    handleEnterKey(event, videoId) {
      this.handleFileNameUpdate(videoId);
    },
    getProductIdLength(video) {
      return (video.productIds && video.productIds.length) || 0;
    },
    openProductListModal(video) {
      this.$emit("open-product-list-modal", video);
    },
    deselectAllVideos() {
      this.selectedVideos = [];
      this.$emit("reset-selected-video");
    },
    openEditChaptersModal(video) {
      this.$emit("open-edit-chapters-modal", video);
    },
    openFilterModal() {
      this.filtersActive = true;
      this.showVideoFilter = true;
    },
    openSortingModal() {
      this.sortingActive = true;
      this.showVideoSort = true;
    },
    openAddTagsModal(video) {
      this.$emit("open-add-tags-modal", video);
    },
    handleFilter(filter) {
      if (filter.tags !== undefined) {
        this.activeFilters.tags = filter.tags;
      }
      if (filter.published !== undefined) {
        this.activeFilters.published =
          filter.published?.value ?? filter.published;
      }
      if (filter.productsTagged !== undefined) {
        this.activeFilters.productsTagged =
          filter.productsTagged?.value ?? filter.productsTagged;
      }
    },
    handleFilterReset() {
      this.activeFilters = { tags: [], published: null, productsTagged: null };
    },
    handleSort({ sortType, isAscending }) {
      this.activeSorting = sortType;
      this.activeOrder = isAscending ? "asc" : "desc";
    },
    closeFilterModal() {
      this.filtersActive = false;
      this.showVideoFilter = false;
    },
    closeSortingModal() {
      this.sortingActive = false;
      this.showVideoSort = false;
    },
    getLocale(flagCode) {
      return locales.find((l) => l.code.replace(/\//g, "") === flagCode);
    },
    getLocaleFlag(flagCode) {
      const loc = this.getLocale(flagCode);
      return loc ? loc.flag : "@/assets/globe-icon.png";
    },
    getLocaleName(flagCode) {
      const loc = this.getLocale(flagCode);
      return loc ? loc.name : "Global";
    },
    openLocalesModal(video) {
      this.$emit("open-locale-modal", video);
    },
    imgKey(video) {
      const src = this.thumbnailSrc(video) || "";
      return `${video._id}-${src}`;
    },
    handleThumbError(video) {
      if (!this.retryOnlyIdsSet.has(video._id)) return;

      const MAX_TRIES = 3;
      const tries = (this.thumbRetryCount[video._id] || 0) + 1;
      this.$set(this.thumbRetryCount, video._id, tries);

      if (!video.thumbnail || tries > MAX_TRIES) return;

      setTimeout(() => {
        const bust = Date.now();
        const sep = video.thumbnail.includes("?") ? "&" : "?";
        const refreshed = `${video.thumbnail}${sep}v=${bust}`;
        this.$set(this.tempThumbsById, video._id, refreshed);
      }, tries * 1000);
    },
    handleThumbLoad(video) {
      this.$set(this.thumbRetryCount, video._id, 0);
    },
    onDocMouseDown(e) {
      if (!this.editingVideoId) return;
      const el = this.$refs.videoFilenameTextarea;
      const node = Array.isArray(el) ? el[0] : el;
      if (node && !node.contains(e.target)) {
        node.blur();
      }
    },
  },
};
</script>

<style lang="scss">
.video-library-modal .sliding-modal {
  width: 608px;
  padding: 20px 0;
}
</style>

<style lang="scss" scoped>
.library-modal-content {
  height: calc(80%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
}

.video-library-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  width: 100%;
  padding: 0 2px;
}
.video-library-title-wrapper {
  display: flex;
  gap: 8px;
}

.video-library-title {
  @include base-font;
  @include font-big;
  text-align: start;
  max-width: 380px;
  display: flex;
  align-items: center;
}

.purple-selected {
  color: $dark-purple;
}

.library-search-bar {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;
}

.sorting-options {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.sorting {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.sort-name {
  @include base-font;
  @include font-small;
}

.selected-sort {
  color: $dark-purple;
}

.sorting-arrows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  transform: scale(70%);
  .sort-arrow {
    &:hover {
      cursor: pointer;
    }
    path {
      fill: $light-grey;
    }
  }
  .reverse-arrow {
    transform: translateX(-0.9px);
    path {
      transform: rotate(180deg);
      transform-origin: 50% 50%;
    }
  }
  .arrow-selected {
    path {
      fill: $dark-purple;
    }
  }
}
.video-library-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  justify-content: start;
  overflow-y: auto;
  gap: 24px;
  width: 100%;
  height: 100vh;
  padding: 0 2px;
  padding-bottom: 120px;
  flex-grow: 1;
  scrollbar-width: none;
  .video-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 150px;
    border-radius: 4px;
    margin-right: 2px;
    &:hover {
      cursor: pointer;
    }
    &.selected {
      .video-image {
        border: 1px solid $dark-purple;
        img {
          background-color: rgba(255, 182, 193, 0.5);
        }
      }
    }
  }
  .video-image {
    // height: 213.33px;
    border-radius: 4px;
    width: 100%;
    border: 1px solid #c4c4c4;
    box-sizing: border-box;
    position: relative;
    background-color: black;
    display: flex;
    align-items: center;
    position: relative;
    overflow: hidden;
    aspect-ratio: 120 / 213.33;
    img {
      width: 100%;
      border-radius: 4px;
    }
  }
  .no-thumbnail {
    background-color: #c4c4c4;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    p {
      font-size: 12px;
      text-align: center;
    }
  }
  .selection-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgba(249, 186, 255, 0.3) 0%,
      rgba(249, 186, 255, 0.3) 100%
    );
    z-index: 100;
  }

  .purple-circle {
    border-radius: 50%;
    height: 12px;
    width: 12px;
    background-color: $light-purple;
    z-index: 150;
    position: absolute;
    top: 8px;
    right: 8px;
    border: 1px solid $dark-purple;
  }

  .video-details {
    width: fit-content;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    border-radius: 2px;
    background-color: rgba(129, 129, 129, 0.8);
    position: absolute;
    left: 4px;
    z-index: 50;
    span {
      font-size: 10px;
      font-weight: 700;
      color: white;
    }
  }
  .products-tagged {
    bottom: 24px;
    &:hover {
      background-color: #7b01b5;
    }
  }
  .chapters {
    bottom: 24px;
    right: 4px;
    left: unset;
    &:hover {
      background-color: #7b01b5;
    }
  }

  .globe-details {
    bottom: 24px;
    right: 4px;
    left: unset;
    z-index: 50;
    &:hover {
      background-color: #7b01b5;
    }
  }

  .moved-chapters {
    bottom: 44px;
  }

  .video-tags {
    bottom: 44px;
    &:hover {
      background-color: #7b01b5;
    }
  }

  .video-views {
    bottom: 4px;
  }

  .video-duration {
    bottom: 4px;
    right: 4px;
    left: unset;
  }
  .video-card-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .video-card-title {
    @include font-smaller;
    gap: 16px;
    width: 120px;
    height: 30px;
    overflow: hidden;
    display: inline-block;
    text-overflow: ellipsis;
  }
  .video-card-indication {
    font-size: 11px;
    color: $light-grey;
    span {
      font-weight: 600;
    }
  }
  .play-icon {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 50;
    &:focus {
      outline: none;
    }
    &:hover .icon-path {
      fill: #7b01b5;
    }
    .icon-path {
      fill: rgba(129, 129, 129, 0.8);
    }
  }
  scrollbar-width: none;
}

.few-video-cards {
  display: flex !important;
  justify-content: flex-start !important;
}

.play-icon-selected-video {
  .icon-path {
    fill: #7b01b5 !important;
  }
}

.video-library-cards::-webkit-scrollbar {
  width: 0;
}

.video-library-cards::-webkit-scrollbar-track {
  background-color: transparent;
}

.video-library-cards::-webkit-scrollbar-thumb {
  background-color: transparent;
}
.buttons {
  z-index: 100;
  width: 608px;
  position: fixed;
  bottom: 0;
  background-color: white;
  min-width: 415px;
  display: flex;
  justify-content: center;
  height: fit-content;
  gap: 32px;
  padding: 16px 0;
  justify-self: flex-end;
}

@media (max-width: 1250px) {
  .modal-background {
    width: 45%;
  }
  .video-library-cards {
    gap: 16px;
  }
  .library-search-bar {
    width: 60%;
  }
}

.file-name-container {
  width: 120px;
  height: 28px;
}

.video-filename-textarea {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  width: 120px;
  height: 28px;
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
  border: none;
  resize: none;
  box-sizing: border-box;
  outline: 1px solid $light-grey;
  border-radius: 2px;
}

.selected-videos {
  display: flex;
  align-items: center;
  gap: 8px;
}
.square-cross-icon {
  cursor: pointer;
}

.no-videos-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.no-videos-snippet-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.no-videos-snippet {
  border: 1px dotted $dark-grey;
  padding: 24px;
}

.no-videos-text {
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 16px;
  h2 {
    font-size: 16px;
    font-weight: 700;
  }
  p {
    font-size: 14px;
  }
  span {
    font-weight: 700;
    display: block;
  }
}

.no-video-img {
  width: 100%;
  margin-top: 14px;
}

.filter-sort-button {
  display: flex;
  flex: 0 0 auto;
  width: 87px;
  gap: 8px;
  align-items: center;
  justify-content: center;
  border: 1px solid $dark-grey;
  border-radius: 2px;
  padding: 4px 8px;
  height: 30px;
  box-sizing: border-box;
  &:hover {
    background: #f1f1f6;
    cursor: pointer;
    svg {
      path {
        fill: $dark-grey;
      }
    }
  }
}
.filter-sort-by {
  @include font-smaller;
  color: $dark-grey;
  &:hover {
    color: $dark-grey;
  }
}

.selected-filter-sort-button {
  background: $dark-purple;
  svg {
    path {
      fill: white;
    }
  }
  p {
    color: white;
  }
}
.filter-icon {
  position: relative;
}

.filter-svg {
  path {
    fill: $dark-grey;
  }
}
.number-filters {
  position: absolute;
  left: 8px;
  font-size: 6px;
  line-height: 7px;
  text-align: center;
  align-items: center;
  width: 10px;
  height: 10px;
  font-weight: 500;
  background-color: $dark-purple;
  color: white;
  padding: 2px 1px 1px 1px;
  border-radius: 50%;
  box-sizing: border-box;
}

.sort-button {
  margin-right: 2px;
}

.sort-icon {
  position: relative;
}
.sort-arrows {
  display: flex;
  flex-direction: column;
  gap: 4px;
  transform: scale(50%);
  margin-right: 1px;
  path {
    fill: $dark-grey;
  }
}
.reverse-arrow {
  transform: translateX(-0.9px);
  path {
    transform: rotate(180deg);
    transform-origin: 50% 50%;
  }
}
.number-sorting {
  position: absolute;
  top: 3px;
  left: 10px;
  font-size: 6px;
  line-height: 7px;
  text-align: center;
  align-items: center;
  width: 10px;
  height: 10px;
  font-weight: 500;
  background-color: $dark-purple;
  color: white;
  padding: 2px 1px 1px 1px;
  border-radius: 50%;
  box-sizing: border-box;
}

.search-filter-sort-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.library-compact {
  display: grid;
  gap: 8px;
  padding-right: 20px;
  padding-left: 15px;
}

.globe-icon {
  width: 10px;
  height: 10px;
}
</style>
